import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, MessageSquare, ShoppingBag, Clock, CheckCircle, AlertCircle, TrendingUp, DollarSign, Calendar } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS, CATEGORY_LABELS } from '@/lib/index';
import { useAuth } from '@/hooks/useAuth';
import { Layout } from '@/components/Layout';
import { supabase } from '@/integrations/supabase/client';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

interface UserCredits {
  credits_remaining: number;
  credits_purchased: number;
  purchase_date: string;
  expiry_date: string;
  is_expired: boolean;
}

interface Transaction {
  id: string;
  package: string;
  amount: number;
  credits_added: number;
  payment_method: string;
  status: string;
  created_at: string;
}

interface CreditUsage {
  id: string;
  credits_used: number;
  action_type: string;
  question_id: string;
  created_at: string;
}

interface Question {
  id: string;
  question: string;
  category: string;
  answer: string | null;
  status: string;
  is_anonymous: boolean;
  created_at: string;
}

export default function ForumMyQuestionsPage() {
  const { isLoggedIn, userId } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [creditUsage, setCreditUsage] = useState<CreditUsage[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      loadUserData();
    }
  }, [userId]);

  const loadUserData = async () => {
    try {
      setLoading(true);

      // Load credits
      const { data: creditsData } = await supabase
        .from('user_credits_2026_03_28')
        .select('*')
        .eq('user_id', userId)
        .single();

      setCredits(creditsData);

      // Load transactions
      const { data: transactionsData } = await supabase
        .from('credit_transactions_2026_03_28')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      setTransactions(transactionsData || []);

      // Load credit usage
      const { data: usageData } = await supabase
        .from('credit_usage_2026_03_28')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      setCreditUsage(usageData || []);

      // Load questions
      const { data: questionsData } = await supabase
        .from('forum_questions_2026_03_28')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      setQuestions(questionsData || []);

    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalSpent = transactions
    .filter(t => t.status === 'completed')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalCreditsUsed = creditUsage.reduce((sum, u) => sum + u.credits_used, 0);

  // Show login prompt if not logged in
  if (!isLoggedIn) {
    return (
      <Layout>
        <div className="min-h-screen bg-background pt-[140px] flex items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Prijavite se</CardTitle>
              <CardDescription>
                Morate biti prijavljeni da biste videli svoju istoriju.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate(ROUTE_PATHS.FORUM_CREDITS)} className="w-full">
                Idite na Prijavu
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-background pt-[140px] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Učitavanje...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background pt-[140px]">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-accent/5 pointer-events-none" />
        
        <div className="container mx-auto px-4 py-16 relative">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              📊 Moj Profil
            </h1>
            <p className="text-lg text-muted-foreground">
              Kompletna istorija vaših aktivnosti
            </p>
          </motion.div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <motion.div {...fadeInUp}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Preostalo Kredita
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-primary">
                    {credits?.credits_remaining || 0}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    od {credits?.credits_purchased || 0} kupljenih
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div {...fadeInUp}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Ukupno Potrošeno
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    {totalSpent.toLocaleString('sr-RS')} RSD
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {transactions.filter(t => t.status === 'completed').length} transakcija
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div {...fadeInUp}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Potrošeno Kredita
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-orange-600">
                    {totalCreditsUsed}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {questions.length} pitanja
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div {...fadeInUp}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {credits?.is_expired ? (
                    <Badge variant="destructive" className="text-lg">
                      Isteklo
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-lg bg-green-100 text-green-800">
                      Aktivno
                    </Badge>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Važi do: {credits?.expiry_date ? new Date(credits.expiry_date).toLocaleDateString('sr-RS') : 'N/A'}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="questions" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="questions">Pitanja ({questions.length})</TabsTrigger>
              <TabsTrigger value="transactions">Transakcije ({transactions.length})</TabsTrigger>
              <TabsTrigger value="usage">Potrošnja ({creditUsage.length})</TabsTrigger>
              <TabsTrigger value="stats">Statistika</TabsTrigger>
            </TabsList>

            {/* Questions Tab */}
            <TabsContent value="questions">
              <Card>
                <CardHeader>
                  <CardTitle>Moja Pitanja</CardTitle>
                  <CardDescription>Sva vaša postavljena pitanja</CardDescription>
                </CardHeader>
                <CardContent>
                  {questions.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground mb-4">Još niste postavili nijedno pitanje</p>
                      <Button onClick={() => navigate(ROUTE_PATHS.FORUM_ASK)}>
                        Postavi Pitanje
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {questions.map((q) => (
                        <Card key={q.id}>
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <Badge variant="outline" className="mb-2">
                                  {CATEGORY_LABELS[q.category as keyof typeof CATEGORY_LABELS]}
                                </Badge>
                                <p className="text-sm font-medium">{q.question}</p>
                              </div>
                              <Badge
                                variant={
                                  q.status === 'answered' ? 'secondary' :
                                  q.status === 'pending' ? 'outline' : 'destructive'
                                }
                              >
                                {q.status === 'answered' && '✅ Odgovoreno'}
                                {q.status === 'pending' && '⏳ Na čekanju'}
                                {q.status === 'rejected' && '❌ Odbijeno'}
                              </Badge>
                            </div>
                          </CardHeader>
                          {q.answer && (
                            <CardContent>
                              <div className="bg-muted p-4 rounded-lg">
                                <p className="text-sm font-medium mb-2">Odgovor:</p>
                                <p className="text-sm">{q.answer}</p>
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions">
              <Card>
                <CardHeader>
                  <CardTitle>Istorija Transakcija</CardTitle>
                  <CardDescription>Sve vaše kupovine kredita</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Paket</TableHead>
                        <TableHead>Krediti</TableHead>
                        <TableHead>Iznos</TableHead>
                        <TableHead>Način Plaćanja</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground">
                            Nema transakcija
                          </TableCell>
                        </TableRow>
                      ) : (
                        transactions.map((t) => (
                          <TableRow key={t.id}>
                            <TableCell>
                              {new Date(t.created_at).toLocaleDateString('sr-RS')}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {t.package === 'starter' && 'Starter'}
                                {t.package === 'popularan' && 'Popularan'}
                                {t.package === 'vip' && 'VIP'}
                                {t.package === 'premium' && 'Premium'}
                              </Badge>
                            </TableCell>
                            <TableCell>{t.credits_added} kredita</TableCell>
                            <TableCell className="font-bold">
                              {t.amount.toLocaleString('sr-RS')} RSD
                            </TableCell>
                            <TableCell>
                              {t.payment_method === 'ips_qr' && '📱 IPS QR'}
                              {t.payment_method === 'bank_slip' && '📄 Uplatnica'}
                            </TableCell>
                            <TableCell>
                              {t.status === 'completed' && (
                                <Badge variant="secondary" className="bg-green-100 text-green-800">
                                  ✅ Potvrđeno
                                </Badge>
                              )}
                              {t.status === 'pending' && (
                                <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                                  ⏳ Pending
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Usage Tab */}
            <TabsContent value="usage">
              <Card>
                <CardHeader>
                  <CardTitle>Istorija Potrošnje</CardTitle>
                  <CardDescription>Kako ste trošili kredite</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Akcija</TableHead>
                        <TableHead>Potrošeno Kredita</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {creditUsage.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground">
                            Nema potrošnje
                          </TableCell>
                        </TableRow>
                      ) : (
                        creditUsage.map((u) => (
                          <TableRow key={u.id}>
                            <TableCell>
                              {new Date(u.created_at).toLocaleDateString('sr-RS', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </TableCell>
                            <TableCell>
                              {u.action_type === 'question_posted' && '❓ Postavljeno pitanje'}
                              {u.action_type === 'bonus' && '🎁 Bonus kredit'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">
                                {u.credits_used} kredit{u.credits_used > 1 ? 'a' : ''}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Stats Tab */}
            <TabsContent value="stats">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Finansijska Statistika</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Ukupno uplaćeno:</span>
                      <span className="font-bold text-lg">{totalSpent.toLocaleString('sr-RS')} RSD</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Broj transakcija:</span>
                      <span className="font-bold">{transactions.filter(t => t.status === 'completed').length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Prosečna uplata:</span>
                      <span className="font-bold">
                        {transactions.filter(t => t.status === 'completed').length > 0
                          ? Math.round(totalSpent / transactions.filter(t => t.status === 'completed').length).toLocaleString('sr-RS')
                          : 0} RSD
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Statistika Kredita</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Ukupno kupljeno:</span>
                      <span className="font-bold text-lg">{credits?.credits_purchased || 0} kredita</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Potrošeno:</span>
                      <span className="font-bold">{totalCreditsUsed} kredita</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Preostalo:</span>
                      <span className="font-bold text-primary">{credits?.credits_remaining || 0} kredita</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Iskorišćenost:</span>
                      <span className="font-bold">
                        {credits?.credits_purchased
                          ? Math.round((totalCreditsUsed / credits.credits_purchased) * 100)
                          : 0}%
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Statistika Pitanja</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Ukupno pitanja:</span>
                      <span className="font-bold">{questions.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Odgovoreno:</span>
                      <span className="font-bold text-green-600">
                        {questions.filter(q => q.status === 'answered').length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Na čekanju:</span>
                      <span className="font-bold text-yellow-600">
                        {questions.filter(q => q.status === 'pending').length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Stopa odgovora:</span>
                      <span className="font-bold">
                        {questions.length > 0
                          ? Math.round((questions.filter(q => q.status === 'answered').length / questions.length) * 100)
                          : 0}%
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Članstvo</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Datum kupovine:</span>
                      <span className="font-bold">
                        {credits?.purchase_date
                          ? new Date(credits.purchase_date).toLocaleDateString('sr-RS')
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Važi do:</span>
                      <span className="font-bold">
                        {credits?.expiry_date
                          ? new Date(credits.expiry_date).toLocaleDateString('sr-RS')
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Status:</span>
                      {credits?.is_expired ? (
                        <Badge variant="destructive">Isteklo</Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-green-100 text-green-800">
                          Aktivno
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          {/* Quick Actions */}
          <motion.div className="mt-8 flex gap-4 justify-center" {...fadeInUp}>
            <Button onClick={() => navigate(ROUTE_PATHS.FORUM_ASK)} size="lg">
              <MessageSquare className="w-4 h-4 mr-2" />
              Postavi Pitanje
            </Button>
            <Button onClick={() => navigate(ROUTE_PATHS.FORUM_CREDITS)} variant="outline" size="lg">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Kupi Kredite
            </Button>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}